/**
 * Keeps an `<iframe>` only when it embeds a video from a trusted player, and
 * removes every other iframe together with its content.
 *
 * `markdownSanitizeSchema` lets `iframe` through with the attributes in
 * {@link TRUSTED_EMBED_ATTRIBUTES}. The schema alone cannot decide which
 * iframe is safe. An attribute rule in `hast-util-sanitize` compares the raw
 * `src` string with a literal or a regular expression, and a string is not a
 * parsed URL: the raw prefix `https://www.youtube.com` also matches
 * `https://www.youtube.com.evil.example/embed/`. This plugin parses `src` with
 * `URL` and compares the protocol, the hostname and the path prefix.
 *
 * A protocol-relative `src` (`//www.youtube.com/embed/…`) resolves against
 * `https:` and passes the same check. A kept iframe's `src` is the absolute URL
 * the check approved, so what renders is exactly what was judged.
 *
 * A kept iframe keeps only the attributes a player needs. The sanitiser's
 * global attribute list also lets `id`, `name` and `tabIndex` through, so this
 * plugin removes every attribute that is not on the list. The iframe's
 * children go too: they are fallback text, and a browser that supports
 * iframes does not render them.
 */

/** A video player an iframe may embed: its exact hostname and path prefix. */
const TRUSTED_EMBEDS: ReadonlyArray<{ readonly hostname: string; readonly pathPrefix: string }> = [
  { hostname: 'www.youtube.com', pathPrefix: '/embed/' },
  { hostname: 'youtube.com', pathPrefix: '/embed/' },
  { hostname: 'www.youtube-nocookie.com', pathPrefix: '/embed/' },
  { hostname: 'player.vimeo.com', pathPrefix: '/video/' },
];

/** The attributes a kept iframe carries, as hast property names. */
export const TRUSTED_EMBED_ATTRIBUTES = [
  'src',
  'width',
  'height',
  'title',
  'allow',
  'allowFullScreen',
  'frameBorder',
  'loading',
  'referrerPolicy',
] as const;

const TRUSTED_ATTRIBUTE_NAMES: ReadonlySet<string> = new Set(TRUSTED_EMBED_ATTRIBUTES);

/**
 * A `src` as the URL the check judges. A protocol-relative `src` resolves
 * against `https:`. Any other `src` without a scheme does not parse, because
 * `URL` gets no base, so the check does not trust it.
 *
 * `URL` throws on a string it cannot parse. `URL.canParse` avoids the `try`, but
 * Safari ships it only from version 17, and this pipeline also runs in the
 * browser. `Option.liftThrowable` is not an option either: `@vs-shop/ui` does
 * not depend on `effect`.
 */
const parseEmbedSrc = (src: string): URL | null => {
  try {
    return new URL(src.startsWith('//') ? `https:${src}` : src);
  } catch {
    return null;
  }
};

/** The approved URL of a trusted video embed, or `null` for any other `src`. */
const trustedEmbedUrl = (src: unknown): URL | null => {
  if (typeof src !== 'string') return null;

  const url = parseEmbedSrc(src);

  return url !== null &&
    url.protocol === 'https:' &&
    url.port === '' &&
    url.username === '' &&
    url.password === '' &&
    TRUSTED_EMBEDS.some(
      ({ hostname, pathPrefix }) => url.hostname === hostname && url.pathname.startsWith(pathPrefix)
    )
    ? url
    : null;
};

type HastElement = {
  type: 'element';
  tagName: string;
  properties: Record<string, unknown>;
  children: unknown[];
};

const isIframe = (node: unknown): node is HastElement =>
  typeof node === 'object' &&
  node !== null &&
  'type' in node &&
  node.type === 'element' &&
  'tagName' in node &&
  node.tagName === 'iframe' &&
  'properties' in node &&
  typeof node.properties === 'object' &&
  node.properties !== null &&
  'children' in node &&
  Array.isArray(node.children);

const hasChildren = (node: unknown): node is { children: unknown[] } =>
  typeof node === 'object' && node !== null && 'children' in node && Array.isArray(node.children);

const keepTrustedEmbeds = (node: unknown): void => {
  if (!hasChildren(node)) return;

  node.children = node.children.flatMap((child) => {
    if (!isIframe(child)) {
      keepTrustedEmbeds(child);
      return [child];
    }

    const url = trustedEmbedUrl(child.properties.src);
    if (url === null) return [];

    child.properties = {
      ...Object.fromEntries(
        Object.entries(child.properties).filter(([name]) => TRUSTED_ATTRIBUTE_NAMES.has(name))
      ),
      src: url.href,
    };
    child.children = [];
    return [child];
  });
};

export const rehypeTrustedEmbeds = () => (tree: unknown) => {
  keepTrustedEmbeds(tree);
};
